import '@ncdai/react-wheel-picker/style.css';

import * as WheelPickerPrimitive from '@ncdai/react-wheel-picker';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

const WHEEL_PICKER_STYLES = `
  [data-rwp-root] {
    perspective: none !important;
    transform-style: flat !important;
  }
  [data-rwp-option-item] {
    backface-visibility: visible !important;
    opacity: 1 !important;
    transition: transform 0.2s ease-out;
  }
  [data-rwp-highlight-wrapper] {
    z-index: 10 !important;
  }
`;

type WheelPickerValue = WheelPickerPrimitive.WheelPickerValue;

type WheelPickerOption<T extends WheelPickerValue = string> =
  WheelPickerPrimitive.WheelPickerOption<T>;

type WheelPickerClassNames = WheelPickerPrimitive.WheelPickerClassNames;

function WheelPickerWrapper({
  className,
  ...props
}: ComponentProps<typeof WheelPickerPrimitive.WheelPickerWrapper>) {
  return (
    <>
      <style>{WHEEL_PICKER_STYLES}</style>
      <WheelPickerPrimitive.WheelPickerWrapper
        className={cn(
          'flex rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950 antialiased',
          '*:data-rwp:first:*:data-rwp-highlight-wrapper:rounded-s-xl',
          '*:data-rwp:last:*:data-rwp-highlight-wrapper:rounded-e-xl',
          className
        )}
        {...props}
      />
    </>
  );
}

function WheelPicker<T extends WheelPickerValue = string>({
  classNames,
  className,
  ...props
}: WheelPickerPrimitive.WheelPickerProps<T> & { className?: string }) {
  return (
    <div className={cn('relative h-full w-full', className)}>
      <WheelPickerPrimitive.WheelPicker
        classNames={{
          optionItem: cn(
            'text-zinc-500 dark:text-zinc-400 data-disabled:opacity-40 text-center w-full font-bold transition-colors',
            'text-[17px] leading-tight antialiased [text-rendering:optimizeLegibility] [font-smoothing:antialiased]',
            '!opacity-100 !blur-none !filter-none',
            classNames?.optionItem
          ),
          highlightWrapper: cn(
            'bg-zinc-50 text-zinc-950 dark:bg-zinc-900 dark:text-zinc-50 border-y border-zinc-200/50 dark:border-zinc-800/50',
            'data-rwp-focused:ring-2 data-rwp-focused:ring-primary/20 data-rwp-focused:ring-inset',
            classNames?.highlightWrapper
          ),
          highlightItem: cn('data-disabled:opacity-40', classNames?.highlightItem),
        }}
        {...props}
      />
    </div>
  );
}

export { WheelPicker, WheelPickerWrapper };
export type { WheelPickerClassNames, WheelPickerOption };
